import React, { useEffect, useMemo, useState } from 'react';
import { getApiBaseUrl } from '../utils/apiBaseUrl';
import { useAuth } from '../contexts/AuthContext';
import { getUserLevel } from '../utils/userLevel';
import MrtDialog from './MrtDialog';
import GroupTree from '../pages/GroupTree';
import { DASHBOARD_CARD_REGISTRY } from '../config/dashboardCardRegistry';
import './AdminUserGroupMembersModal.css';

function safeText(v) {
  if (v === null || typeof v === 'undefined') return '';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v); } catch (e) { return String(v); }
}

function AgmTh({ className, label, tech }) {
  return (
    <th className={className} title={tech ? `${label} · ${tech}` : label}>
      <span className="agm-th-main">{label}</span>
      {tech ? <span className="agm-th-tech">{tech}</span> : null}
    </th>
  );
}

function truncateUid(s, max = 14) {
  const t = String(s || '').trim();
  if (!t) return '';
  return t.length > max ? `${t.slice(0, max)}…` : t;
}

/** 그룹 ID → 로그인(kakao) → 이름 (빈 값은 각 키에서 뒤로) */
function sortGroupMemberRows(list) {
  const key = (v) => {
    const s = String(v ?? '').trim();
    return s === '' ? '\uffff' : s;
  };
  return [...(list || [])].sort((a, b) => {
    let c = key(a?.member_id).localeCompare(key(b?.member_id), 'ko', { sensitivity: 'base' });
    if (c !== 0) return c;
    c = key(a?.kakao).localeCompare(key(b?.kakao), 'ko', { sensitivity: 'base' });
    if (c !== 0) return c;
    c = key(a?.name).localeCompare(key(b?.name), 'ko', { sensitivity: 'base' });
    if (c !== 0) return c;
    return String(a?.seq ?? '').localeCompare(String(b?.seq ?? ''), 'ko', { numeric: true });
  });
}

function AgmSaveIcon() {
  return (
    <svg className="agm-save-btn__ico" viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M17 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-4-4zm-5 16c-1.66 0-3-1.34-3-3s1.34-3 3-3 3 1.34 3 3-1.34 3-3 3zm3-10H6V5h9v4z"
      />
    </svg>
  );
}

function AgmSaveSpinnerIcon() {
  return (
    <svg className="agm-save-btn__ico agm-save-btn__ico--spin" viewBox="0 0 24 24" width="17" height="17" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="2.25" strokeDasharray="42 28" strokeLinecap="round" />
    </svg>
  );
}

/** API workspace_perms: null/미파싱=미설정, 배열=화이트리스트 */
function normalizeRowWorkspacePerms(row) {
  const w = row?.workspace_perms;
  if (w === null || typeof w === 'undefined') return null;
  if (Array.isArray(w)) return w;
  if (typeof w === 'string') {
    try {
      const p = JSON.parse(w);
      return Array.isArray(p) ? p : null;
    } catch (e) {
      return null;
    }
  }
  return null;
}

function isFullWorkspaceWhitelist(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return false;
  // bypassWorkspacePerms 카드는 항상 표시되므로 whitelist 판단에서 제외
  return DASHBOARD_CARD_REGISTRY.filter((c) => !c.bypassWorkspacePerms).every((c) => arr.includes(c.id));
}

export default function AdminUserGroupMembersModal({ open, onClose }) {
  const apiUrl = getApiBaseUrl();
  const { user } = useAuth();
  const token = (user && typeof user === 'object' && user.token) ? String(user.token) : '';
  const authHeaders = useMemo(() => (token ? { Authorization: `Bearer ${token}` } : {}), [token]);
  const myLevel = useMemo(() => getUserLevel(user), [user]);
  const canEdit = (myLevel != null && myLevel <= 2);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState('');
  const [hideUnapproved, setHideUnapproved] = useState(true);
  const [tab, setTab] = useState('active'); // active | inactive
  const [memberGroup, setMemberGroup] = useState('ALL'); // member_id 기준 그룹 필터
  // ✅ (2) 행 단위 그룹 변경(트리)
  const [rowGroupPickOpen, setRowGroupPickOpen] = useState(false);
  const [rowGroupPickSeq, setRowGroupPickSeq] = useState('');
  const [rowGroupPickName, setRowGroupPickName] = useState('');
  const [savingSeqs, setSavingSeqs] = useState(() => ({})); // { [seq]: true }
  const [dirtySeqs, setDirtySeqs] = useState(() => ({})); // { [seq]: true } 저장 전 임시 변경 추적
  const [saveError, setSaveError] = useState('');
  const [signupWorking, setSignupWorking] = useState(false);
  const [signupMsg, setSignupMsg] = useState('');
  const [wsPermOpen, setWsPermOpen] = useState(false);
  const [wsPermSeq, setWsPermSeq] = useState('');
  const [wsPermName, setWsPermName] = useState('');
  const [wsPermUnrestricted, setWsPermUnrestricted] = useState(false);
  const [wsPermPick, setWsPermPick] = useState(() => ({}));
  const [uidExpanded, setUidExpanded] = useState(() => ({}));
  const [uidCopiedSeq, setUidCopiedSeq] = useState('');
  const [clipHint, setClipHint] = useState('');

  const copyUid = async (seq, text) => {
    const sid = String(seq || '').trim();
    const t = String(text || '').trim();
    if (!t) return;
    setClipHint('');
    try {
      await navigator.clipboard.writeText(t);
      setUidCopiedSeq(sid);
      window.setTimeout(() => setUidCopiedSeq((cur) => (cur === sid ? '' : cur)), 1600);
    } catch (e) {
      setClipHint('UID 복사에 실패했습니다. 브라우저 권한을 확인해 주세요.');
      window.setTimeout(() => setClipHint(''), 3200);
    }
  };

  const toggleUidExpand = (seq) => {
    const sid = String(seq || '').trim();
    if (!sid) return;
    setUidExpanded((m) => ({ ...(m || {}), [sid]: !m?.[sid] }));
  };

  const openWorkspacePermEditor = (r) => {
    const seq = String(r?.seq ?? '').trim();
    if (!seq) return;
    const norm = normalizeRowWorkspacePerms(r);
    const list = Array.isArray(norm) ? norm : [];
    const unrestricted = isFullWorkspaceWhitelist(list);
    setWsPermSeq(seq);
    setWsPermName(String(r?.name || '').trim() || seq);
    setWsPermUnrestricted(unrestricted);
    const pick = {};
    // bypassWorkspacePerms 카드는 체크박스에서 제외 (항상 표시됨)
    DASHBOARD_CARD_REGISTRY.filter((c) => !c.bypassWorkspacePerms).forEach((c) => {
      pick[c.id] = unrestricted ? true : list.includes(c.id);
    });
    setWsPermPick(pick);
    setWsPermOpen(true);
  };

  const applyWorkspacePerms = async () => {
    const sid = String(wsPermSeq || '').trim();
    if (!sid) return;
    setSaveError('');
    // bypassWorkspacePerms 카드는 항상 표시되므로 workspace_perms 저장 대상 제외
    const controllableIds = DASHBOARD_CARD_REGISTRY.filter((c) => !c.bypassWorkspacePerms).map((c) => c.id);
    if (wsPermUnrestricted) {
      await saveRow(sid, { workspacePerms: controllableIds });
    } else {
      const ids = controllableIds.filter((id) => wsPermPick[id]);
      await saveRow(sid, { workspacePerms: ids });
    }
    setWsPermOpen(false);
  };

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiUrl}/api/admin_user/group_members?_=${Date.now()}`, { method: 'GET', headers: { ...authHeaders } });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json || json.success !== true) {
        const detail = (json && (json.detail || json.message)) ? (json.detail || json.message) : `http_${res.status}`;
        throw new Error(String(detail));
      }
      setRows(Array.isArray(json.data) ? json.data : []);
    } catch (e) {
      setError(String(e?.message || e));
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setQ('');
    setHideUnapproved(true);
    setTab('active');
    setMemberGroup('ALL');
    setRowGroupPickOpen(false);
    setRowGroupPickSeq('');
    setRowGroupPickName('');
    setDirtySeqs({});
    setSaveError('');
    setSignupMsg('');
    setUidExpanded({});
    setUidCopiedSeq('');
    setClipHint('');
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const myEmail = useMemo(() => String(user?.email || '').trim().toLowerCase(), [user]);
  const myRow = useMemo(() => {
    if (!myEmail) return null;
    return (Array.isArray(rows) ? rows : []).find((r) => String(r?.kakao || '').trim().toLowerCase() === myEmail) || null;
  }, [rows, myEmail]);
  const hasMyRow = useMemo(() => {
    if (!myEmail) return false;
    return (Array.isArray(rows) ? rows : []).some((r) => String(r?.kakao || '').trim().toLowerCase() === myEmail);
  }, [rows, myEmail]);

  const approvalStatus = String(user?.approval_status || '').trim();
  const derivedApprovalStatus = useMemo(() => {
    // user.approval_status가 로컬에 stale일 수 있어, 목록(rows) 기준으로 보정
    if (myRow) {
      const activatedRaw = myRow?.activated;
      const activated = (activatedRaw === 1 || String(activatedRaw) === '1') ? 1 : 0;
      return activated === 1 ? 'approved' : 'pending';
    }
    return approvalStatus;
  }, [approvalStatus, myRow]);
  const canSignupRequest = useMemo(() => {
    // ✅ 요청: "처음 로그인" + "미가입"이면 가입승인요청 가능
    if (!myEmail) return false;
    if (derivedApprovalStatus === 'not_registered') return true;
    // fallback(이전 로직): 리스트가 비거나 내 계정이 없으면 요청 가능
    const total = (Array.isArray(rows) ? rows.length : 0);
    return (total === 0 || !hasMyRow);
  }, [rows, hasMyRow, myEmail, derivedApprovalStatus]);

  const signupRequest = async () => {
    if (signupWorking) return;
    setSignupMsg('');
    setSaveError('');
    try {
      setSignupWorking(true);
      const kakao = String(user?.email || '').trim();
      const name = String(user?.name || '').trim();
      const res = await fetch(`${apiUrl}/api/admin_user/signup_request?_=${Date.now()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        // ✅ 백엔드가 body 기반으로 INSERT할 때 빈 값('')이 들어가 유니크 충돌(1062)이 나는 케이스 방지
        body: JSON.stringify({ kakao, name }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json || json.success !== true) {
        const detail = (json && (json.detail || json.message)) ? (json.detail || json.message) : `http_${res.status}`;
        throw new Error(String(detail));
      }
      setSignupMsg('가입승인요청이 접수되었습니다. (다음 로그인부터 승인 대기 상태로 전환됩니다)');
      await load();
    } catch (e) {
      setSaveError(String(e?.message || e));
    } finally {
      setSignupWorking(false);
    }
  };

  const updateRowLocal = (seq, patch) => {
    const sid = String(seq ?? '').trim();
    if (sid) setDirtySeqs((m) => ({ ...(m || {}), [sid]: true }));
    setRows((prev) => (Array.isArray(prev) ? prev.map((r) => {
      if (String(r?.seq ?? '') !== String(seq ?? '')) return r;
      return { ...(r || {}), ...(patch || {}) };
    }) : prev));
  };

  const saveRow = async (seq, patch) => {
    const sid = String(seq ?? '').trim();
    if (!sid) return;
    setSaveError('');
    setSavingSeqs((m) => ({ ...(m || {}), [sid]: true }));
    try {
      // ✅ 규칙 강제(프론트): expire=0이면 activated=0
      const nextPatch = { ...(patch || {}) };
      if (nextPatch.expire === 0 || String(nextPatch.expire) === '0') nextPatch.activated = 0;
      const res = await fetch(`${apiUrl}/api/admin_user/update?_=${Date.now()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ seq: Number(seq), ...(nextPatch || {}) }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json || json.success !== true) {
        const detail = (json && (json.detail || json.message)) ? (json.detail || json.message) : `http_${res.status}`;
        throw new Error(String(detail));
      }
      // 저장 성공 후 최신 데이터 재조회(컬럼 fallback 케이스에서도 안정)
      await load();
      setDirtySeqs((m) => {
        const next = { ...(m || {}) };
        delete next[sid];
        return next;
      });
    } catch (e) {
      setSaveError(String(e?.message || e));
    } finally {
      setSavingSeqs((m) => {
        const next = { ...(m || {}) };
        delete next[sid];
        return next;
      });
    }
  };

  const memberGroups = useMemo(() => {
    const list = (Array.isArray(rows) ? rows : [])
      .map((r) => String(r?.member_id ?? '').trim())
      .filter((x) => !!x);
    const uniq = Array.from(new Set(list)).sort((a, b) => a.localeCompare(b));
    return uniq;
  }, [rows]);

  const filtered = useMemo(() => {
    const query = String(q || '').trim().toLowerCase();
    const base = (Array.isArray(rows) ? rows : []).filter((r) => {
      const sid = String(r?.seq ?? '').trim();
      const isDirty = !!(sid && dirtySeqs && dirtySeqs[sid]);
      const expRaw = r?.expire;
      const exp = (expRaw === 0 || String(expRaw) === '0') ? 0 : 1;
      // ✅ 저장 전에는 행이 갑자기 사라지지 않게: 더티 행은 탭 필터 예외
      if (!isDirty) {
        if (tab === 'active' && exp === 0) return false;
        if (tab === 'inactive' && exp !== 0) return false;
      }
      const activatedRaw = r?.activated;
      const activated = (activatedRaw === 1 || String(activatedRaw) === '1') ? 1 : 0;
      // ✅ 비활성 탭에서는 미승인 숨김을 적용하지 않음(비활성=미승인이므로)
      if (tab === 'active' && hideUnapproved && activated === 0) return false;
      const gid = String(r?.member_id ?? '').trim();
      if (memberGroup !== 'ALL' && gid !== memberGroup) return false;
      return true;
    });

    const matched = !query
      ? base
      : base.filter((r) => {
        const s = [
          r?.seq,
          r?.name,
          r?.kakao,
          r?.activated,
          r?.member_id,
          r?.member_name,
          r?.group_member_uid_hex,
          Array.isArray(r?.workspace_perms) ? r.workspace_perms.join(',') : r?.workspace_perms,
        ].map((x) => String(x ?? '').toLowerCase()).join(' | ');
        return s.includes(query);
      });
    return sortGroupMemberRows(matched);
  }, [rows, q, hideUnapproved, memberGroup, tab, dirtySeqs]);

  return (
    <>
    <MrtDialog
      open={!!open}
      ariaLabel="그룹 관리"
      onClose={onClose}
      zIndex={32000}
      panelStyle={{
        width: 'min(1580px, 99vw)',
        height: 'min(88vh, 960px)',
        maxHeight: 'min(88vh, 960px)',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        overflow: 'hidden',
      }}
    >
      <div className="agm">
        <header className="agm-header">
          <div className="agm-title-block">
            <h2 className="agm-title">그룹 관리</h2>
            <p className="agm-subtitle agm-subtitle--friendly">구성원의 소속·승인·메뉴 권한을 한곳에서 관리합니다.</p>
          </div>
          <div className="agm-header-actions">
            <button type="button" className="agm-btn agm-btn--ghost" onClick={() => load()} disabled={loading} title="새로고침">
              새로고침
            </button>
            <button type="button" className="agm-btn agm-btn--outline" onClick={() => onClose?.()} title="닫기(ESC)">
              닫기
            </button>
          </div>
        </header>

        <div className="agm-body">
          <div className="agm-toolbar agm-toolbar--single">
            <div className="agm-seg" role="tablist" aria-label="계정 상태">
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'active'}
                className={tab === 'active' ? 'agm-seg--on' : ''}
                onClick={() => setTab('active')}
                title="활성 계정"
              >
                활성
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === 'inactive'}
                className={tab === 'inactive' ? 'agm-seg--on agm-seg--on-danger' : ''}
                onClick={() => setTab('inactive')}
                title="비활성 계정"
              >
                비활성
              </button>
            </div>
            <input
              className="agm-search agm-search--grow"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="이름, 이메일, 그룹, UID로 검색"
            />
            <select
              className="agm-select-field agm-select-field--toolbar"
              value={memberGroup}
              onChange={(e) => setMemberGroup(String(e.target.value || 'ALL'))}
              title="소속 그룹(로그인 ID)으로 필터"
            >
              <option value="ALL">전체 그룹</option>
              {memberGroups.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
            <label
              className={`agm-check${tab === 'inactive' ? ' agm-check--disabled' : ''}`}
              title={tab === 'inactive' ? '비활성 탭은 미승인만 표시되므로 이 옵션은 적용되지 않습니다.' : undefined}
            >
              <input
                type="checkbox"
                checked={!!hideUnapproved}
                disabled={tab === 'inactive'}
                onChange={(e) => setHideUnapproved(!!e.target.checked)}
              />
              미승인 숨김
            </label>
            <div className="agm-count">{loading ? '로딩…' : `표시 ${filtered.length} / 전체 ${rows.length}`}</div>
          </div>

          {clipHint ? (
            <div className="agm-alert agm-alert--err" role="status">
              {clipHint}
            </div>
          ) : null}

          {saveError ? (
            <div className="agm-alert agm-alert--err" role="alert">
              저장 오류: {saveError}
            </div>
          ) : null}

          {signupMsg ? (
            <div className="agm-alert agm-alert--ok" role="status">
              {signupMsg}
            </div>
          ) : null}

          {canSignupRequest ? (
            <div className="agm-alert agm-alert--info agm-signup-row">
              <span>내 계정이 아직 등록되지 않았습니다. 가입승인요청을 진행할 수 있습니다.</span>
              <button
                type="button"
                className="agm-btn agm-btn--primary"
                onClick={() => signupRequest()}
                disabled={signupWorking}
                title="가입승인요청"
              >
                {signupWorking ? '요청중...' : '가입승인요청'}
              </button>
            </div>
          ) : null}

          {error ? (
            <div className="agm-alert agm-alert--err" role="alert">
              {error}
            </div>
          ) : null}

          <div className="agm-table-wrap">
            <div className="agm-table-scroll">
              <table className="agm-table">
                <thead>
                  <tr>
                    <AgmTh className="agm-th--seq" label="번호" tech="seq" />
                    <AgmTh className="agm-th--status" label="계정" tech="expire" />
                    <AgmTh className="agm-th--approval" label="승인" tech="activated" />
                    <AgmTh className="agm-th--name" label="이름" tech="name" />
                    <AgmTh className="agm-th--kakao" label="로그인" tech="kakao" />
                    <AgmTh className="agm-th--role" label="등급" tech="role" />
                    <AgmTh className="agm-th--sm-name" label="소속" tech="member_name" />
                    <AgmTh className="agm-th--sm-id" label="그룹 ID" tech="member_id" />
                    <AgmTh className="agm-th--uid" label="내부 UID" tech="group_member" />
                    <AgmTh className="agm-th--ws" label="메뉴" tech="workspace_perms" />
                    <AgmTh className="agm-th--save" label="저장" />
                  </tr>
                </thead>
              <tbody>
                {(filtered || []).map((r, idx) => {
                  const seq = (r && (r.seq ?? r.id ?? r.user_id)) != null ? String(r.seq ?? r.id ?? r.user_id) : '';
                  const name = safeText(r?.name);
                  const kakao = safeText(r?.kakao);
                  const isProtected = (() => {
                    const n = String(name || '').trim();
                    if (!n) return false;
                    const k = String(kakao || '').trim().toLowerCase();
                    if (k === 'mrt@mrt.co.kr') return true;
                    // ✅ 요청: 정확히 "관리자", "[HW] 고명산"만 수정 불가 (가입요청 계정까지 잠기지 않게 범위 축소)
                    if (n === '관리자') return true;
                    const norm = n.replace(/\s+/g, '');
                    if (n === '[HW] 고명산') return true;
                    if (norm === '[HW]고명산' || norm === 'HW고명산') return true;
                    return false;
                  })();
                  const activatedRaw = r?.activated;
                  const activated = (activatedRaw === 1 || String(activatedRaw) === '1') ? 1 : 0;
                  const roleRaw = r?.role;
                  const role = (roleRaw != null && String(roleRaw).trim() !== '') ? Number(roleRaw) : null;
                  const expireRaw = r?.expire;
                  const expire = (expireRaw === 0 || String(expireRaw) === '0') ? 0 : 1;
                  const memberName = safeText(r?.member_name);
                  const memberId = safeText(r?.member_id);
                  const uidHex = safeText(r?.group_member_uid_hex);
                  const saving = !!savingSeqs[String(seq || '')];
                  const wpNorm = normalizeRowWorkspacePerms(r);
                  const wpList = Array.isArray(wpNorm) ? wpNorm : [];
                  const wpFull = isFullWorkspaceWhitelist(wpList);
                  const wpEmpty = wpList.length === 0;
                  const wpLabel = wpFull ? '전체 허용' : (wpEmpty ? '메뉴 없음' : `${wpList.length}개 메뉴`);
                  const wpBadgeClass = wpFull ? 'agm-ws-badge agm-ws-badge--open' : (wpEmpty ? 'agm-ws-badge agm-ws-badge--none' : 'agm-ws-badge agm-ws-badge--pick');
                  const uidStr = String(uidHex || '').trim();
                  const uidLong = uidStr.length > 16;
                  const uidShow = !uidStr ? '' : (uidExpanded[seq] ? uidStr : truncateUid(uidStr, 14));
                  return (
                    <tr key={`${seq || 'x'}:${idx}`}>
                      <td className="agm-seq">{seq}</td>
                      <td>
                        <select
                          className={`agm-select ${expire === 1 ? 'agm-select--ok' : 'agm-select--bad'}`}
                          value={expire}
                          onChange={(e) => {
                            const v = Number(e.target.value || 1);
                            if (v === 0) updateRowLocal(seq, { expire: 0, activated: 0 });
                            else updateRowLocal(seq, { expire: 1 });
                          }}
                          disabled={isProtected || !canEdit}
                          title={(isProtected || !canEdit) ? '수정 불가' : '활성/비활성'}
                        >
                          <option value={1}>활성</option>
                          <option value={0}>비활성</option>
                        </select>
                      </td>
                      <td>
                        <select
                          className={`agm-select ${activated === 1 ? 'agm-select--ok' : 'agm-select--bad'}`}
                          value={activated}
                          onChange={(e) => {
                            const v = Number(e.target.value || 0);
                            updateRowLocal(seq, { activated: v });
                          }}
                          disabled={isProtected || !canEdit || expire === 0}
                          title={(isProtected || !canEdit) ? '수정 불가' : '승인/미승인'}
                        >
                          <option value={1}>승인</option>
                          <option value={0}>미승인</option>
                        </select>
                      </td>
                      <td className="agm-cell-name" title={name}>
                        {name}{isProtected ? <span className="agm-badge-lock">보호</span> : null}
                      </td>
                      <td className="agm-cell-muted" title={kakao}>
                        {String(kakao || '').trim() || '-'}
                      </td>
                      <td>
                        <select
                          className="agm-select agm-select--narrow"
                          value={role == null ? '' : String(role)}
                          onChange={(e) => {
                            const v = String(e.target.value || '').trim();
                            updateRowLocal(seq, { role: v ? Number(v) : null });
                          }}
                          disabled={isProtected || !canEdit}
                          title={isProtected ? '수정 불가' : '등급 1~9'}
                        >
                          <option value="">-</option>
                          {Array.from({ length: 9 }).map((_, i) => {
                            const v = i + 1;
                            return <option key={v} value={String(v)}>{v}</option>;
                          })}
                        </select>
                      </td>
                      <td className="agm-cell-muted" title={memberName}>
                        {String(memberName || '').trim() || '-'}
                      </td>
                      <td>
                        <div className="agm-cell-flex">
                          <div className="agm-cell-truncate" title={String(memberId || '').trim() || '-'}>
                            {String(memberId || '').trim() || '-'}
                          </div>
                          <button
                            type="button"
                            className="agm-btn-inline agm-btn-inline--soft"
                            disabled={isProtected || !canEdit}
                            onClick={() => {
                              const sid = String(seq || '').trim();
                              if (!sid) return;
                              setRowGroupPickSeq(sid);
                              setRowGroupPickName(String(name || '').trim() || sid);
                              setRowGroupPickOpen(true);
                            }}
                            title={(isProtected || !canEdit) ? '수정 불가' : '그룹변경(트리에서 선택 후 즉시 적용)'}
                          >
                            그룹변경
                          </button>
                        </div>
                      </td>
                      <td className="agm-uid-wrap">
                        <div className={`agm-uid-row${uidExpanded[seq] ? ' agm-uid-row--expanded' : ''}`}>
                          <span
                            className={`agm-uid-text${uidExpanded[seq] ? ' agm-uid-text--expanded' : ''}`}
                            title={uidStr || undefined}
                          >
                            {uidStr ? uidShow : '—'}
                          </span>
                          {uidStr ? (
                            <span className="agm-uid-actions">
                              {uidLong ? (
                                <button
                                  type="button"
                                  className="agm-btn-micro"
                                  onClick={() => toggleUidExpand(seq)}
                                >
                                  {uidExpanded[seq] ? '접기' : '펼치기'}
                                </button>
                              ) : null}
                              <button
                                type="button"
                                className="agm-btn-micro"
                                onClick={() => copyUid(seq, uidStr)}
                              >
                                {uidCopiedSeq === String(seq) ? '복사됨' : '복사'}
                              </button>
                            </span>
                          ) : null}
                        </div>
                      </td>
                      <td className="agm-menu-cell-wrap">
                        <div className="agm-menu-cell">
                          <span className={wpBadgeClass} title={wpFull ? '등록된 메뉴 전부 허용' : (wpEmpty ? '허용된 메뉴 없음(기본·미설정)' : JSON.stringify(wpList))}>
                            {wpLabel}
                          </span>
                          <button
                            type="button"
                            className="agm-btn-inline agm-btn-inline--soft"
                            disabled={isProtected || !canEdit}
                            onClick={() => openWorkspacePermEditor(r)}
                            title={(isProtected || !canEdit) ? '수정 불가' : '대시보드에 보일 메뉴 선택'}
                          >
                            메뉴 설정
                          </button>
                        </div>
                      </td>
                      <td className="agm-save-cell">
                        <button
                          type="button"
                          className="agm-save-btn"
                          disabled={saving || isProtected || !canEdit}
                          onClick={() => saveRow(seq, { activated, role: (role == null ? undefined : role), expire, memberId: String(memberId || '').trim() })}
                          title={(isProtected || !canEdit) ? '수정 불가' : (saving ? '저장 중…' : '이 행 저장')}
                          aria-label={saving ? '저장 중' : '이 행 저장'}
                          aria-busy={!!saving}
                        >
                          {saving ? <AgmSaveSpinnerIcon /> : <AgmSaveIcon />}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </div>
    </MrtDialog>
    <MrtDialog
      open={!!rowGroupPickOpen}
      ariaLabel="계정 그룹 변경(트리)"
      onClose={() => setRowGroupPickOpen(false)}
      zIndex={34000}
      panelStyle={{
        width: 'min(1580px, 99vw)',
        height: 'min(88vh, 960px)',
        maxHeight: 'min(88vh, 960px)',
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="agm agm-sub-header agm-sub-header-row">
        <div className="agm-title-block" style={{ minWidth: 0 }}>
          <h2 className="agm-sub-title" style={{ fontSize: '1rem' }}>그룹 변경</h2>
          <p className="agm-sub-desc agm-sub-desc-ellipsis">대상: {rowGroupPickName}</p>
        </div>
        <button type="button" className="agm-btn agm-btn--ghost" onClick={() => setRowGroupPickOpen(false)}>
          닫기
        </button>
      </div>
      <div className="agm-group-embed">
        <GroupTree
          embedded
          selectMode
          onClose={() => setRowGroupPickOpen(false)}
          onSelectNode={async ({ node }) => {
            const sid = String(rowGroupPickSeq || '').trim();
            if (!sid) return;
            const loginId = String(node?.userid_login_id || '').trim();
            if (!loginId) {
              setSaveError('선택한 그룹에 연결된 ID(userid_login_id)가 없어 적용할 수 없습니다.');
              return;
            }
            // UI 반영 + 즉시 저장(요청: 선택하면 적용)
            updateRowLocal(sid, { member_id: loginId });
            await saveRow(sid, { memberId: loginId });
            setRowGroupPickOpen(false);
          }}
        />
      </div>
    </MrtDialog>

    <MrtDialog
      open={!!wsPermOpen}
      ariaLabel="대시보드 메뉴 권한"
      onClose={() => setWsPermOpen(false)}
      zIndex={36000}
      panelStyle={{ width: 'min(520px, 94vw)', maxHeight: '88vh', padding: 0, overflow: 'hidden' }}
    >
      <div className="agm agm-sub-header">
        <h2 className="agm-sub-title">대시보드 메뉴</h2>
        <p className="agm-sub-desc">{wsPermName}</p>
      </div>
      <div className="agm-ws-scroll">
        <label className="agm-ws-check-row">
          <input
            type="checkbox"
            checked={wsPermUnrestricted}
            onChange={(e) => {
              const on = !!e.target.checked;
              setWsPermUnrestricted(on);
              if (on) {
                setWsPermPick((prev) => {
                  const next = { ...(prev || {}) };
                  DASHBOARD_CARD_REGISTRY.forEach((c) => { next[c.id] = true; });
                  return next;
                });
              }
            }}
          />
          모든 메뉴 허용
        </label>
        <p className="agm-ws-hint">
          기본은 메뉴를 표시하지 않습니다. 아래에서 허용할 항목만 선택하거나, 위에서 전체 허용을 켜 주세요. 저장 후 해당 계정은 다시 로그인하면 반영됩니다.
        </p>
        <div className={`agm-ws-list${wsPermUnrestricted ? ' agm-ws-list--disabled' : ''}`}>
          {DASHBOARD_CARD_REGISTRY.filter((c) => !c.bypassWorkspacePerms).map((c) => (
            <label key={c.id} className="agm-ws-item">
              <input
                type="checkbox"
                checked={!!wsPermPick[c.id]}
                onChange={(e) => setWsPermPick((p) => ({ ...(p || {}), [c.id]: !!e.target.checked }))}
              />
              <span>
                <span className="agm-ws-item-title">
                  <span style={{ marginRight: 6 }}>{c.icon}</span>
                  {c.title}
                </span>
                <span className="agm-ws-item-id">{c.id}</span>
              </span>
            </label>
          ))}
        </div>
      </div>
      <div className="agm-sub-footer">
        <button type="button" className="agm-btn agm-btn--ghost" onClick={() => setWsPermOpen(false)}>
          취소
        </button>
        <button type="button" className="agm-btn agm-btn--primary" onClick={() => applyWorkspacePerms()}>
          적용·저장
        </button>
      </div>
    </MrtDialog>
    </>
  );
}


